import { defineComponent } from "vue"
import Tabs from "@/components/uikit/Tabs/index.vue"
import GlobalChat from "@/components/chat/desktop/Global.vue"
import AdminGlobalChat from "@/components/chat/desktop/Admin/AdminGlobal.vue"
import ModeratorGlobalChat from "@/components/chat/desktop/Moderator/ModeratorGlobal.vue"
import { TabType } from "@/components/uikit/Tabs/types"
import Arrow from "@/components/icons/Arrow.vue"
import { AsyncState, Configs, GiveawayType } from "@/store/types"
import Slider from "@/components/uikit/Slider.vue"
import { useApplyElementHeight } from "@/composables/useApplyElementHeight"
import { waitForElement } from "@/composables/useWaitForElement"
import { GameSlug, GiveawayStatus } from "@/services/enums"
import { callApi } from "@/services/api"
import { bus } from "@/utils"

export default defineComponent({
  components: {
    Tabs,
    GlobalChat,
    Arrow,
    Slider,
    AdminGlobalChat,
    ModeratorGlobalChat,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    async isOpen(newVal: boolean) {
      if (newVal) {
        const response = await callApi.giveaway.get(
          "all" as GameSlug,
          GiveawayStatus.STARTED
        )

        this.chatGiveaways = response.data.data

        document.documentElement.classList.add("chat-open")
      } else {
        document.documentElement.classList.remove("chat-open")
      }
    },
    $route() {
      if (this.isMobile) {
        this.$emit("close")
      }
    },
  },
  data() {
    return {
      applyHeight: null as ReturnType<typeof useApplyElementHeight> | null,
      TAWK_ID: process.env.VUE_APP_TAWK_ID || "",
      activeTab: "global",
      chatGiveaways: [] as GiveawayType<GameSlug>[],
    }
  },
  methods: {
    changeTab(tab: TabType<string>) {
      this.activeTab = tab.value
    },
    async updateGiveaways(giveaways: AsyncState<GiveawayType<GameSlug>[]>) {
      this.chatGiveaways = giveaways.data
    },
  },
  computed: {
    giveaways(): AsyncState<GiveawayType[]> {
      return this.$store.getters["giveaways/data"]
    },
    hasGiveawaysBanner() {
      return !!this.chatGiveaways.length
    },
    isActiveTabSystem() {
      return this.activeTab === "system"
    },
    chatTabs(): TabType<string>[] {
      if (this.$route.path.startsWith("/admin")) {
        return [
          { active: true, value: "global", label: "Chat" },
        ] as TabType<string>[]
      }
      return [
        { active: true, value: "global", label: "Chat" },
        {
          active: false,
          value: "system",
          label: "Bloxup",
        },
      ] as TabType<string>[]
    },
    globalConfig(): AsyncState<Configs> {
      return this.$store.getters["global/data"]
    },
  },
  unmounted() {
    document.documentElement.classList.remove("chat-open")

    bus.off("giveaway:updated", this.updateGiveaways)
  },
  mounted() {
    bus.on("giveaway:updated", this.updateGiveaways)
    this.$store.dispatch("giveaways/getGiveaways", {
      game_slug: "all",
      status: GiveawayStatus.STARTED,
    })
    waitForElement({
      selector: "iframe",
      rootDocument: document.querySelector(".chat-panel") || window.document,
      timeoutMs: 3000,
      onNotFound: () => {
        const chatTrigger: HTMLElement | null =
          document.querySelector(".chat-trigger")

        if (chatTrigger) chatTrigger.style.display = "block"
      },
      onFound: (el) => {
        // Apply max-height to iframe parent
        const iframeParent = el.parentElement
        if (iframeParent) {
          if (this.$props.isMobile) {
            iframeParent.style.setProperty("max-width", "100dvw", "important")
            iframeParent.style.setProperty("min-width", "100dvw", "important")
          } else {
            iframeParent.style.setProperty("min-height", "100vh", "important")
            iframeParent.style.setProperty("min-height", "100vh", "important")
          }
        }

        const iframeDoc =
          (el as HTMLIFrameElement).contentDocument ||
          (el as HTMLIFrameElement).contentWindow?.document

        if (iframeDoc) {
          const style = iframeDoc.createElement("style")
          style.textContent = `
            input,
            textarea,
            select {
              font-size: 16px !important;
            }
          `

          if (iframeDoc.head) {
            iframeDoc.head.appendChild(style)
          } else {
            iframeDoc.appendChild(style)
          }
        }

        iframeParent?.classList.add("iframe-wrapper")

        this.applyHeight = useApplyElementHeight({
          selector: ".tawk-max-container",
          transform: this.$props.isMobile
            ? "calc(100dvh - 360px)"
            : "calc(100% - 100px)",
          timeoutMs: 10000,
          intervalMs: 200,
          rootDocument:
            (el as HTMLIFrameElement).contentDocument ||
            (el as HTMLIFrameElement).contentWindow?.document,
        })

        this.applyHeight.waitAndApply()
        this.applyHeight.apply()

        window.addEventListener("resize", this.applyHeight.apply)

        const chatTrigger: HTMLElement | null =
          document.querySelector(".chat-trigger")

        if (chatTrigger) chatTrigger.style.display = "block"
      },
    })
  },
  beforeUnmount() {
    this.applyHeight?.stop()
    if (this.applyHeight) {
      window.removeEventListener("resize", this.applyHeight.apply)
    }
  },
})
